import { useEffect } from 'react';

export type SeoStructuredData = Record<string, unknown> | Record<string, unknown>[];

export type SeoHeadProps = {
  title: string;
  description: string;
  keywords: string;
  path: string;
  ogType?: string;
  structuredData?: SeoStructuredData;
};

const OG_IMAGE =
  'https://readdy.ai/api/search-image?query=dramatic%20dark%20editorial%20cover%20with%20abstract%20ink%20texture%20and%20crimson%20light%20streak%20across%20a%20shadowy%20city%20skyline%20silhouette%2C%20investigative%20journalism%20mood%2C%20high%20contrast%2C%20cinematic%20grain%2C%20no%20text&width=1200&height=630&seq=st-share-cover-2026&orientation=landscape';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function SeoHead({
  title,
  description,
  keywords,
  path,
  ogType = 'website',
  structuredData,
}: SeoHeadProps) {
  useEffect(() => {
    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', 'index, follow');

    const origin = window.location.origin;
    const url = `${origin}${path}`;

    upsertLink('canonical', url);

    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:site_name', '深瞳爆料');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', OG_IMAGE);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', OG_IMAGE);

    const existing = document.getElementById('page-structured-data');
    if (existing) {
      existing.remove();
    }

    if (structuredData) {
      const script = document.createElement('script');
      script.id = 'page-structured-data';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, ogType, structuredData]);

  return null;
}